import { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

import { AuthProvider } from "./lib/auth";
import Header from "./components/Header";
import Footer from "./components/Footer";
import MobileStickyBar from "./components/MobileStickyBar";
import Chatbot from "./components/Chatbot";
import InstallPrompt from "./components/InstallPrompt";
import TopContactStrip from "./components/TopContactStrip";
import CompareTray from "./components/CompareTray";
import Home from "./pages/Home";
import InventoryList from "./pages/InventoryList";
import InventorySearchResults from "./pages/InventorySearchResults";
import VehicleDetail from "./pages/VehicleDetail";
import BuildYourOwnDeal from "./pages/BuildYourOwnDeal";
import BuildMyOwnDeal from "./pages/BuildMyOwnDeal";
import ProtectionProductsHub from "./pages/ProtectionProductsHub";
import ProtectionProductPage from "./pages/ProtectionProductPage";
import ValueMyTrade from "./pages/ValueMyTrade";
import FindMeVehicle from "./pages/FindMeVehicle";
import PaymentHelp from "./pages/PaymentHelp";
import EvHelp from "./pages/EvHelp";
import VehicleAlerts from "./pages/VehicleAlerts";
import JustListed from "./pages/JustListed";
import Reviews from "./pages/Reviews";
import Compare from "./pages/Compare";
import SavedVehicles from "./pages/SavedVehicles";
import ScheduleTestDrive from "./pages/ScheduleTestDrive";
import AboutScott from "./pages/AboutScott";
import Contact from "./pages/Contact";
import ChatPage from "./pages/ChatPage";
import Faq from "./pages/Faq";
import { ShopByNeedHub, ShopByNeedDetail } from "./pages/ShopByNeed";
// Legacy ServiceAreas pages are lazy-loaded so the dealership-identifying
// LocalBusiness schema (street address, dealership name, hasMap URL) lives in
// a separate chunk and never ships in the bundle for /chevy-sales-* pages.
const ServiceAreasModule = lazy(() => import("./pages/ServiceAreas").then((m) => ({ default: m.ServiceAreasHub })));
const CityPageLegacy    = lazy(() => import("./pages/ServiceAreas").then((m) => ({ default: m.CityPage })));
import ChevySalesCity from "./pages/ChevySalesCity";
import CentralOhioHub from "./pages/CentralOhioHub";
import { ALL_CITY_SLUGS } from "./data/cityData";
import { FinanceTradeHub, ResourcesHub } from "./pages/Hubs";
import {
  Privacy, Terms, Accessibility, Sitemap, NotFound,
  PaymentCalculator, TradeInChecklist, LeaseHelp, FinanceHelp,
  NewVsUsed, CarBuyingTips,
} from "./pages/SmallPages";
import Admin from "./pages/Admin";
import CustomerPortal from "./pages/CustomerPortal";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookieNotice from "./components/CookieNotice";
import SoldMarquee from "./components/SoldMarquee";
import DealerShell, { TodayPage, PipelinePage, ConversationsPage, ReportsPage, AutoDripPage, PermissionsPage } from "./pages/DealerShell";
import { SecurityDashboardPage, SecurityPosturePage } from "./pages/Security";
import usePresenceHeartbeat from "./lib/usePresenceHeartbeat";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import ForgotPassword from "./pages/auth/ForgotPassword";
import DealerLogin from "./pages/auth/DealerLogin";
import MyAccount from "./pages/MyAccount";
import DealerPortal from "./pages/DealerPortal";
import AdminCrmSearch from "./pages/AdminCrmSearch";
import AdminCustomerProfile from "./pages/AdminCustomerProfile";
import AdminMiscDocuments from "./pages/AdminMiscDocuments";
import AdminDocumentTemplates from "./pages/AdminDocumentTemplates";
import AdminDocumentTemplatesBulk from "./pages/AdminDocumentTemplatesBulk";
import AdminDocumentTemplateEditor from "./pages/AdminDocumentTemplateEditor";
import AdminGeneratedFormView from "./pages/AdminGeneratedFormView";
import PdfFormView from "./pages/PdfFormView";
import AdminQuickPacks from "./pages/AdminQuickPacks";
import AdminVautoFeed from "./pages/AdminVautoFeed";
import AdminRebates from "./pages/AdminRebates";
import AdminByersPricing from "./pages/AdminByersPricing";
import AdminFees from "./pages/AdminFees";
import AdminIncentives from "./pages/AdminIncentives";
import AdminSms from "./pages/AdminSms";
import RequireAdmin from "./components/RequireAdmin";
import LeadSuccessReviewCta from "./components/LeadSuccessReviewCta";
import AdminBackupCenter from "./pages/AdminBackupCenter";
import AdminChatbot from "./pages/AdminChatbot";
import CustomerUploadPortal from "./pages/CustomerUploadPortal";

/* Wrapper used when a route is also a model/category filter on /new-vehicles or /used-vehicles */
const NewList = (props) => <InventoryList {...props} baseFilters={{ status: "new", ...(props.baseFilters || {}) }} />;
const UsedList = (props) => <InventoryList {...props} baseFilters={{ status: "used", ...(props.baseFilters || {}) }} />;

/* /inventory entry: ?search=XXX → global search results UI; otherwise the
   existing unified inventory list (with status tabs). */
function InventoryEntry() {
  const loc = useLocation();
  const sp = new URLSearchParams(loc.search);
  if ((sp.get("search") || sp.get("q") || "").trim()) {
    return <InventorySearchResults />;
  }
  return (
    <InventoryList
      pageTitle="Inventory Search"
      h1="Search New and Used Inventory"
      seoTitle="Search New & Used Vehicle Inventory | Scott Sells Chevys"
      seoDescription="Search new and used vehicles in Central Ohio. Filter by model, price, mileage, body style, drivetrain, fuel type, and features."
      showStatusTabs
    />
  );
}

function Layout({ children }) {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/dealer");
  const isBare = loc.pathname.startsWith("/u/"); // customer mobile upload portal — no chrome
  const hideChrome = isAdmin || isBare;
  return (
    <div className="App ssc-mobile-pad">
      {!hideChrome && <SoldMarquee />}
      {/* v6.4 — top utility bar removed per design refresh. Header is now the
          single top-of-page chrome element. */}
      {!hideChrome && <Header />}
      {children}
      {!hideChrome && <Footer />}
      {!hideChrome && <MobileStickyBar />}
      {!hideChrome && <CompareTray />}
      {!hideChrome && <Chatbot />}
      {!hideChrome && <InstallPrompt />}
      {!hideChrome && <CookieNotice />}
      <LeadSuccessReviewCta />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PresenceWrapper>
        <Layout>
        <Suspense fallback={<div className="ssc-container ssc-section">Loading...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/portal" element={<CustomerPortal />} />
            <Route path="/portal/:code" element={<CustomerPortal />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms-of-use" element={<TermsOfService />} />

            {/* New Vehicles */}
            <Route path="/new-vehicles" element={
              <NewList
                pageTitle="New Vehicles"
                h1="New Vehicles in Central Ohio"
                seoTitle="New Chevrolet Vehicles in Central Ohio | Scott Sells Chevys"
                seoDescription="Shop new Chevrolet cars, trucks, SUVs, and EVs with Scott Hall. Serving Grove City, Columbus, Dublin, Hilliard, Westerville, and Central Ohio."
                intro="Browse live new Chevrolet inventory, updated daily from the dealer feed."
              />
            } />
            <Route path="/new-vehicles/cars" element={<NewList pageTitle="New Chevrolet Cars" h1="New Chevrolet Cars" baseFilters={{ body_style: "car" }} />} />
            <Route path="/new-vehicles/suvs" element={<NewList pageTitle="New Chevrolet SUVs" h1="New Chevrolet SUVs" baseFilters={{ body_style: "suv" }} />} />
            <Route path="/new-vehicles/trucks" element={<NewList pageTitle="New Chevrolet Trucks" h1="New Chevrolet Trucks" baseFilters={{ body_style: "truck" }} />} />
            <Route path="/new-vehicles/evs" element={<NewList pageTitle="New Chevrolet EVs" h1="New Chevrolet EVs" baseFilters={{ body_style: "ev" }} />} />
            <Route path="/new-vehicles/commercial" element={<NewList pageTitle="Commercial & Work Trucks" h1="Commercial & Work Vehicles" seoTitle="Commercial Vans & Work Trucks in Central Ohio | Scott Sells Chevys" seoDescription="Shop Express vans, Silverado HD, chassis cabs, cargo vans, and work-ready commercial vehicles with Scott Hall in Central Ohio." baseFilters={{ body_style: "commercial" }} />} />
            <Route path="/new-vehicles/specials" element={<NewList pageTitle="New Vehicle Specials" h1="New Vehicle Specials" baseFilters={{ is_special: true }} />} />
            <Route path="/new-vehicles/lease-options" element={<NewList pageTitle="New Lease Options" h1="New Lease Options" baseFilters={{ is_lease_offer: true }} />} />
            <Route path="/new-vehicles/silverado" element={<NewList pageTitle="New Silverado" h1="New Chevrolet Silverado" baseFilters={{ model: "Silverado" }} />} />
            <Route path="/new-vehicles/colorado" element={<NewList pageTitle="New Colorado" h1="New Chevrolet Colorado" baseFilters={{ model: "Colorado" }} />} />
            <Route path="/new-vehicles/equinox" element={<NewList pageTitle="New Equinox" h1="New Chevrolet Equinox" baseFilters={{ model: "Equinox" }} />} />
            <Route path="/new-vehicles/equinox-ev" element={<NewList pageTitle="New Equinox EV" h1="New Chevrolet Equinox EV" baseFilters={{ model: "Equinox EV" }} />} />
            <Route path="/new-vehicles/traverse" element={<NewList pageTitle="New Traverse" h1="New Chevrolet Traverse" baseFilters={{ model: "Traverse" }} />} />
            <Route path="/new-vehicles/blazer" element={<NewList pageTitle="New Blazer" h1="New Chevrolet Blazer" baseFilters={{ model: "Blazer" }} />} />
            <Route path="/new-vehicles/trax" element={<NewList pageTitle="New Trax" h1="New Chevrolet Trax" baseFilters={{ model: "Trax" }} />} />
            <Route path="/new-vehicles/tahoe" element={<NewList pageTitle="New Tahoe" h1="New Chevrolet Tahoe" baseFilters={{ model: "Tahoe" }} />} />
            <Route path="/new-vehicles/suburban" element={<NewList pageTitle="New Suburban" h1="New Chevrolet Suburban" baseFilters={{ model: "Suburban" }} />} />
            {/* Vehicle detail */}
            <Route path="/new-vehicles/:slug" element={<VehicleDetail />} />

            {/* Used Vehicles */}
            <Route path="/used-vehicles" element={
              <UsedList
                pageTitle="Used Vehicles"
                h1="Used Vehicles in Central Ohio"
                seoTitle="Used Cars, Trucks & SUVs in Central Ohio | Scott Sells Chevys"
                seoDescription="Shop used cars, trucks, SUVs, fresh trades, one-owner vehicles, low-mileage vehicles, and used vehicles under $25,000 with Scott Hall in Central Ohio."
              />
            } />
            <Route path="/used-vehicles/cars" element={<UsedList pageTitle="Used Cars" h1="Used Cars" baseFilters={{ body_style: "car" }} />} />
            <Route path="/used-vehicles/suvs" element={<UsedList pageTitle="Used SUVs" h1="Used SUVs" baseFilters={{ body_style: "suv" }} />} />
            <Route path="/used-vehicles/trucks" element={<UsedList pageTitle="Used Trucks" h1="Used Trucks" baseFilters={{ body_style: "truck" }} />} />
            <Route path="/used-vehicles/evs" element={<UsedList pageTitle="Used EVs" h1="Used EVs" baseFilters={{ body_style: "ev" }} />} />
            <Route path="/used-vehicles/under-20000" element={<UsedList pageTitle="Used Under $20,000" h1="Used Vehicles Under $20,000" baseFilters={{ max_price: 20000 }} />} />
            <Route path="/used-vehicles/under-25000" element={<UsedList pageTitle="Used Under $25,000" h1="Used Vehicles Under $25,000" baseFilters={{ max_price: 25000 }} />} />
            <Route path="/used-vehicles/low-mileage" element={<UsedList pageTitle="Low Mileage Used" h1="Low Mileage Used Vehicles" baseFilters={{ max_mileage: 35000 }} />} />
            <Route path="/used-vehicles/fresh-trades" element={<UsedList pageTitle="Fresh Trades" h1="Fresh Trades" baseFilters={{ is_fresh_trade: true }} />} />
            <Route path="/used-vehicles/one-owner" element={<UsedList pageTitle="One-Owner Used" h1="One-Owner Used Vehicles" baseFilters={{ one_owner: true }} />} />
            <Route path="/used-vehicles/awd-4x4" element={<UsedList pageTitle="Used AWD & 4x4" h1="Used AWD & 4x4" baseFilters={{ drivetrain: "awd-4x4" }} />} />
            <Route path="/used-vehicles/:slug" element={<VehicleDetail />} />

            {/* Unified inventory — when ?search= is present, render the
                global search results UI with cross-status tabs. Otherwise
                fall back to the existing inventory list with showStatusTabs. */}
            <Route path="/inventory" element={<InventoryEntry />} />

            {/* Legacy URL redirects (client-side <Navigate replace>). Crawlers
                hitting these top-level paths should be served by the backend
                /api/legacy-redirect/* endpoints when ingress is configured. */}
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/contact-scott" element={<Navigate to="/contact" replace />} />
            <Route path="/text-scott" element={<Navigate to="/contact" replace />} />
            <Route path="/payment" element={<Navigate to="/payment-help" replace />} />
            <Route path="/payments" element={<Navigate to="/payment-help" replace />} />
            <Route path="/payment-options" element={<Navigate to="/payment-help" replace />} />
            <Route path="/trade" element={<Navigate to="/value-my-trade" replace />} />
            <Route path="/trade-in" element={<Navigate to="/value-my-trade" replace />} />
            <Route path="/value-trade" element={<Navigate to="/value-my-trade" replace />} />
            <Route path="/build-your-deal" element={<Navigate to="/scotts-deal-desk" replace />} />
            <Route path="/build-a-deal" element={<Navigate to="/scotts-deal-desk" replace />} />
            <Route path="/byod" element={<Navigate to="/scotts-deal-desk" replace />} />
            <Route path="/about" element={<Navigate to="/about-scott" replace />} />
            <Route path="/faqs" element={<Navigate to="/faq" replace />} />
            <Route path="/inventory-search" element={<Navigate to="/inventory" replace />} />
            <Route path="/search-inventory" element={<Navigate to="/inventory" replace />} />
            <Route path="/new-inventory" element={<Navigate to="/new-vehicles" replace />} />
            <Route path="/used-inventory" element={<Navigate to="/used-vehicles" replace />} />
            <Route path="/cars" element={<Navigate to="/new-vehicles/cars" replace />} />
            <Route path="/suvs" element={<Navigate to="/new-vehicles/suvs" replace />} />
            <Route path="/trucks" element={<Navigate to="/new-vehicles/trucks" replace />} />
            <Route path="/evs" element={<Navigate to="/new-vehicles/evs" replace />} />

            {/* Shop by Need */}
            <Route path="/shop-by-need" element={<ShopByNeedHub />} />
            <Route path="/shop-by-need/:topic" element={<ShopByNeedDetail />} />

            {/* Finance & Trade */}
            <Route path="/finance-trade" element={<FinanceTradeHub />} />
            <Route path="/payment-help" element={<PaymentHelp />} />
            <Route path="/value-my-trade" element={<ValueMyTrade />} />
            <Route path="/scotts-deal-desk" element={<BuildMyOwnDeal />} />
            <Route path="/build-my-own-deal" element={<BuildMyOwnDeal />} />
            {/* Legacy route — kept as-is to preserve any indexed/saved links.
                Same component renders the new Build My Own Deal wizard. */}
            <Route path="/build-your-own-deal" element={<BuildMyOwnDeal />} />
            {/* Old Build My Own Deal implementation kept reachable for QA
                comparison via an explicit URL — not linked anywhere. */}
            <Route path="/legacy-deal-desk" element={<BuildYourOwnDeal />} />
            <Route path="/protection-products" element={<ProtectionProductsHub />} />
            <Route path="/protection-products/:slug" element={<ProtectionProductPage />} />
            <Route path="/lease-help" element={<LeaseHelp />} />
            <Route path="/finance-help" element={<FinanceHelp />} />
            <Route path="/trade-in-checklist" element={<TradeInChecklist />} />
            <Route path="/payment-calculator" element={<PaymentCalculator />} />

            {/* Resources */}
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/ev-help" element={<EvHelp />} />
            <Route path="/vehicle-alerts" element={<VehicleAlerts />} />
            <Route path="/just-listed" element={<JustListed />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/car-buying-tips" element={<CarBuyingTips />} />
            <Route path="/new-vs-used" element={<NewVsUsed />} />

            {/* Service Areas — lazy so dealership LocalBusiness schema does
                not bundle with /chevy-sales-* pages in production builds. */}
            <Route path="/service-areas" element={<ServiceAreasModule />} />
            <Route
              path="/service-areas/:city"
              element={<LegacyServiceAreaRedirect />}
            />

            {/* City landing pages (new spec) — /chevy-sales-:slug. RR7 requires
                full-segment params, so we register one explicit route per slug. */}
            <Route path="/chevy-sales-central-ohio" element={<CentralOhioHub />} />
            {ALL_CITY_SLUGS.map((s) => (
              <Route key={s} path={`/chevy-sales-${s}`} element={<ChevySalesCity slug={s} />} />
            ))}
            {/* Unknown /chevy-sales-* slugs are redirected home inside the
                global NotFound component (RR7 splat-with-prefix patterns
                don't match reliably). */}

            {/* Tools */}
            <Route path="/find-me-a-vehicle" element={<FindMeVehicle />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/saved-vehicles" element={<SavedVehicles />} />
            <Route path="/schedule-test-drive" element={<ScheduleTestDrive />} />

            {/* Core */}
            <Route path="/about-scott" element={<AboutScott />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/privacy-policy" element={<Privacy />} />
            <Route path="/terms-of-use" element={<Terms />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/sitemap" element={<Sitemap />} />

            {/* Admin */}
            <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
            <Route path="/admin/login" element={<DealerLogin />} />
            <Route element={<RequireAdmin><DealerShell /></RequireAdmin>}>
              <Route path="/admin/today" element={<TodayPage />} />
              <Route path="/admin/pipeline" element={<PipelinePage />} />
              <Route path="/admin/conversations" element={<ConversationsPage />} />
              <Route path="/admin/reports" element={<ReportsPage />} />
              <Route path="/admin/auto-drip" element={<AutoDripPage />} />
              <Route path="/admin/permissions" element={<PermissionsPage />} />
              <Route path="/admin/security" element={<SecurityDashboardPage />} />
              <Route path="/admin/security/posture" element={<SecurityPosturePage />} />
              <Route path="/admin/crm" element={<AdminCrmSearch />} />
              <Route path="/admin/crm/misc" element={<AdminMiscDocuments />} />
              <Route path="/admin/crm/customer/:id" element={<AdminCustomerProfile />} />
              <Route path="/admin/documents/templates" element={<AdminDocumentTemplates />} />
              <Route path="/admin/documents/templates/bulk" element={<AdminDocumentTemplatesBulk />} />
              <Route path="/admin/documents/templates/:templateId/edit" element={<AdminDocumentTemplateEditor />} />
              <Route path="/admin/documents/quick-packs" element={<AdminQuickPacks />} />
              <Route path="/admin/inventory/vauto-feed" element={<AdminVautoFeed />} />
              <Route path="/admin/rebates" element={<AdminRebates />} />
              <Route path="/admin/byers-pricing" element={<AdminByersPricing />} />
              <Route path="/admin/system/fees" element={<AdminFees />} />
              <Route path="/admin/incentives" element={<AdminIncentives />} />
              <Route path="/admin/sms" element={<AdminSms />} />
              <Route path="/admin/system/backups" element={<AdminBackupCenter />} />
              <Route path="/admin/chatbot" element={<AdminChatbot />} />
            </Route>
            {/* PDF / form viewers stay outside the shell so they get a clean fullscreen viewport */}
            <Route path="/admin/crm/customer/:id/deal-jacket/form" element={<AdminGeneratedFormView />} />
            <Route path="/admin/crm/customer/:id/deal-jacket/pdf-form" element={<PdfFormView />} />
            <Route path="/u/:token" element={<CustomerUploadPortal />} />

            {/* Customer accounts */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/my-account" element={<MyAccount />} />

            {/* Dealer portal */}
            <Route path="/dealer-login" element={<DealerLogin />} />
            <Route path="/dealer" element={<DealerPortal />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </Layout>
        </PresenceWrapper>
      </AuthProvider>
    </BrowserRouter>
  );
}

function PresenceWrapper({ children }) {
  usePresenceHeartbeat();
  return children;
}

/**
 * Redirect /service-areas/:city → /chevy-sales-:city when the city slug
 * exists in the new spec. Otherwise fall back to the legacy page so we
 * never 404 customers on bookmarked links.
 */
function LegacyServiceAreaRedirect() {
  const loc = useLocation();
  const m = loc.pathname.match(/^\/service-areas\/([^/]+)\/?$/);
  const city = m?.[1]?.toLowerCase();
  if (city && ALL_CITY_SLUGS.includes(city)) {
    return <Navigate to={`/chevy-sales-${city}${loc.search}`} replace />;
  }
  return <CityPageLegacy />;
}

export default App;
